// Convert pasted rich HTML → Markdown while preserving formatting
// (bold, italic, headings, lists, tables via GFM, links, and inline colours
// via raw <span style="color:..."> which flows through rehype-raw at render time).
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';

const service = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
  emDelimiter: '*',
  strongDelimiter: '**',
});
service.use(gfm); // tables, strikethrough, task lists

// Preserve inline colour / background-colour on <span>. Markdown has no
// colour syntax, so we keep the raw HTML — the renderer uses rehype-raw.
service.addRule('inlineColorSpan', {
  filter: (node) => {
    if (node.nodeName !== 'SPAN') return false;
    const style = (node.getAttribute && node.getAttribute('style')) || '';
    if (/mso-/i.test(style)) return false; // Excel/Office noise
    return /(^|;)\s*color\s*:/i.test(style) || /(^|;)\s*background(-color)?\s*:/i.test(style);
  },
  replacement: (content, node) => {
    const style = node.getAttribute('style') || '';
    const keep = style
      .split(';')
      .map((s) => s.trim())
      .filter((s) => /^(color|background(-color)?)\s*:/i.test(s) && !/mso-/i.test(s))
      .join('; ');
    return keep ? `<span style="${keep}">${content}</span>` : content;
  },
});

// Underline is common when pasting from Docs — keep as raw <u>.
service.addRule('underline', {
  filter: ['u'],
  replacement: (content) => (content ? `<u>${content}</u>` : ''),
});

// Google Docs wraps everything in <b style="font-weight: normal"> — strip those.
service.addRule('googleDocsWrapper', {
  filter: (node) =>
    node.nodeName === 'B' &&
    /font-weight\s*:\s*normal/i.test(node.getAttribute('style') || ''),
  replacement: (content) => content,
});

// Drop <style>, <script>, <meta>, <link>, <colgroup>, <col>, <title>.
service.remove(['style', 'script', 'meta', 'link', 'colgroup', 'col', 'title']);

/**
 * Sanitize pasted HTML from Excel / Word / Google Sheets before conversion.
 * Excel dumps a mess: MSO conditional comments, .xl63 class blocks, borders
 * against `windowtext`, colgroups, presentational attributes. We strip all
 * of that so the resulting markdown table renders cleanly.
 */
function sanitizeOfficeHtml(html) {
  let out = html;
  // 1. Office conditional comments + any HTML comments (MSO stuffs CSS in comments).
  out = out.replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '');
  out = out.replace(/<!--[\s\S]*?-->/g, '');
  // 2. Remove <style>, <script>, <meta>, <link>, <colgroup>/<col>, <title> blocks.
  out = out.replace(/<(style|script|meta|link|colgroup|col|title)\b[^>]*>[\s\S]*?<\/\1>/gi, '');
  out = out.replace(/<(meta|link|col)\b[^>]*\/?>/gi, '');
  // 3. Strip Excel-only class attributes ("xl63 xl64 …").
  out = out.replace(/\sclass="[^"]*\bxl\d+[^"]*"/gi, '');
  // 4. Keep only web-safe style declarations, drop MSO / windowtext borders.
  out = out.replace(/\sstyle="([^"]*)"/gi, (m, css) => {
    const cleaned = css
      .split(';')
      .map((s) => s.trim())
      .filter((s) => s && !/mso-/i.test(s) && !/windowtext/i.test(s))
      .filter((s) => /^(color|background(-color)?|font-weight|font-style|text-decoration|text-align)\s*:/i.test(s))
      .join('; ');
    return cleaned ? ` style="${cleaned}"` : '';
  });
  // 5. Drop legacy presentational attributes on tables / cells.
  out = out.replace(/\s(border|cellpadding|cellspacing|width|height|valign|align|nowrap|bgcolor)="[^"]*"/gi, '');
  // 6. Collapse duplicated &nbsp; runs.
  out = out.replace(/(&nbsp;\s*){2,}/g, '&nbsp;');
  return out;
}

export function htmlToMarkdown(html) {
  if (!html) return '';
  const clean = sanitizeOfficeHtml(html);
  return service.turndown(clean).trim();
}
